import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Sparkles, ShieldCheck, ShieldX, FileText, Eye, ChevronDown, ChevronRight, AlertCircle, Trash2 } from "lucide-react";
import { Card, CardHead, Btn, Badge, Avatar, Spinner } from "@/components/ui-kit";
import api, { API } from "@/lib/api";
import CertifiedBadge from "@/components/CertifiedBadge";
import { useAuth } from "@/context/AuthContext";

const DOC_TYPES = [
  { key: "photo", label: "Profile photo" },
  { key: "registration", label: "Council / Registration certificate" },
  { key: "membership", label: "Professional membership proof" },
  { key: "insurance", label: "Malpractice / indemnity insurance" },
  { key: "degree", label: "Degree certificate" },
  { key: "id_proof", label: "Government ID proof" },
  { key: "address_proof", label: "Address proof" },
];
const REQUIRED = ["photo", "registration", "membership", "insurance"];
const CLINICIANS = ["Standard", "Senior", "Specialist"];

function statusTone(s) {
  return s === "Verified" ? "emerald" : s === "Rejected" ? "rose" : s === "Review" ? "amber" : "slate";
}

function openDoc(docId) {
  const token = localStorage.getItem("praniq_token");
  window.open(`${API}/documents/${docId}/file?auth=${token}`, "_blank");
}

function PhotoThumb({ docId, name }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoked;
    if (docId) {
      api.get(`/documents/${docId}/file`, { responseType: "blob" })
        .then((r) => { const u = URL.createObjectURL(r.data); revoked = u; setUrl(u); })
        .catch(() => {});
    }
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [docId]);
  if (url) return <img src={url} alt={name} className="w-9 h-9 rounded-full object-cover shrink-0" data-testid="dir-photo-thumb" />;
  return <Avatar name={name} size={36} />;
}

function DocRow({ doc, onChange }) {
  const [busy, setBusy] = useState(false);
  const [num, setNum] = useState(doc.number || "");
  const [exp, setExp] = useState(doc.expiry || "");
  const type = DOC_TYPES.find((d) => d.key === doc.doc_type);
  const ai = doc.ai_result;

  const aiVerify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/documents/${doc.id}/ai-verify`);
      toast.success(`AI: ${data.ai_suggestion === "Verified" ? "looks valid ✓" : "needs review"}`);
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "AI verification failed"); }
    setBusy(false);
  };
  const setStatus = async (s) => {
    await api.put(`/documents/${doc.id}`, { verification_status: s });
    toast.success(`Marked ${s}`); onChange();
  };
  const saveFields = async () => {
    await api.put(`/documents/${doc.id}`, { number: num, expiry: exp });
    toast.success("Document details saved"); onChange();
  };
  const del = async () => {
    if (!window.confirm(`Permanently delete "${type?.label || doc.doc_type}"? This cannot be undone.`)) return;
    setBusy(true);
    try { await api.delete(`/documents/${doc.id}`); toast.success("Document deleted"); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); setBusy(false); }
  };

  return (
    <div className="border border-slate-200 rounded-xl p-3" data-testid={`doc-${doc.id}`}>
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center shrink-0"><FileText size={16} /></div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-slate-800 truncate">{type?.label || doc.doc_type}</div>
          <div className="text-[11.5px] text-slate-400 truncate">{doc.original_filename}</div>
        </div>
        <Badge tone={statusTone(doc.verification_status)}>{doc.verification_status}</Badge>
      </div>
      {ai && (
        <div className="mt-2 text-[11.5px] bg-violet-50 text-violet-700 rounded-lg px-2.5 py-2 flex gap-1.5" data-testid={`doc-ai-${doc.id}`}>
          <Sparkles size={13} className="shrink-0 mt-0.5" />
          <span>AI: {ai.valid ? "appears valid" : "not confident"} ({Math.round((ai.confidence || 0) * 100)}%){ai.notes ? ` — ${ai.notes}` : ""}</span>
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 mt-2.5">
        <label className="block"><span className="pq-label block mb-1">Document / reg. no.</span>
          <input className="pq-input py-1.5" value={num} onChange={(e) => setNum(e.target.value)} placeholder="—" data-testid={`doc-number-${doc.id}`} /></label>
        <label className="block"><span className="pq-label block mb-1">Expiry</span>
          <input className="pq-input py-1.5" value={exp} onChange={(e) => setExp(e.target.value)} placeholder="YYYY-MM-DD" data-testid={`doc-expiry-${doc.id}`} /></label>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2.5">
        <Btn size="sm" onClick={() => openDoc(doc.id)} data-testid={`doc-view-${doc.id}`}><Eye size={13} /> View</Btn>
        <Btn size="sm" onClick={saveFields} data-testid={`doc-save-${doc.id}`}>Save</Btn>
        <Btn size="sm" onClick={aiVerify} disabled={busy} data-testid={`doc-aiverify-${doc.id}`}>{busy ? <Spinner className="w-3.5 h-3.5" /> : <><Sparkles size={13} /> AI verify</>}</Btn>
        <Btn size="sm" className="border-emerald-300 text-emerald-700" onClick={() => setStatus("Verified")} data-testid={`doc-approve-${doc.id}`}><ShieldCheck size={13} /> Approve</Btn>
        <Btn size="sm" variant="danger" onClick={() => setStatus("Rejected")} data-testid={`doc-reject-${doc.id}`}><ShieldX size={13} /> Reject</Btn>
        <Btn size="sm" variant="danger" onClick={del} disabled={busy} data-testid={`doc-delete-${doc.id}`}><Trash2 size={13} /> Delete</Btn>
      </div>
    </div>
  );
}

function PersonDetail({ person, role, onChange }) {
  const [docType, setDocType] = useState("registration");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const [edit, setEdit] = useState({ tier: person.tier || "Standard", city: person.city || "",
    reg_no: person.reg_no || "", membership_no: person.membership_no || "", insurance_no: person.insurance_no || "" });

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("doc_type", docType);
      fd.append("file", file);
      await api.post(`/users/${person.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded"); onChange();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };
  const saveFields = async () => {
    await api.put(`/admin/users/${person.id}`, edit);
    toast.success("Profile updated"); onChange();
  };

  return (
    <div className="bg-slate-50/70 border-t border-slate-200 p-4 space-y-4" data-testid={`detail-${person.id}`}>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="pq-label mb-2">Profile & credential numbers</div>
          <div className="grid grid-cols-2 gap-2.5">
            {role === "physiotherapist" && (
              <label className="block"><span className="pq-label block mb-1">Tier</span>
                <select className="pq-input py-1.5" value={edit.tier} onChange={(e) => setEdit({ ...edit, tier: e.target.value })} data-testid={`edit-tier-${person.id}`}>{CLINICIANS.map((c) => <option key={c}>{c}</option>)}</select></label>
            )}
            <label className="block"><span className="pq-label block mb-1">City</span><input className="pq-input py-1.5" value={edit.city} onChange={(e) => setEdit({ ...edit, city: e.target.value })} data-testid={`edit-city-${person.id}`} /></label>
            <label className="block"><span className="pq-label block mb-1">Registration no.</span><input className="pq-input py-1.5" value={edit.reg_no} onChange={(e) => setEdit({ ...edit, reg_no: e.target.value })} data-testid={`edit-reg-${person.id}`} /></label>
            <label className="block"><span className="pq-label block mb-1">Membership no.</span><input className="pq-input py-1.5" value={edit.membership_no} onChange={(e) => setEdit({ ...edit, membership_no: e.target.value })} data-testid={`edit-membership-${person.id}`} /></label>
            <label className="block col-span-2"><span className="pq-label block mb-1">Insurance policy no.</span><input className="pq-input py-1.5" value={edit.insurance_no} onChange={(e) => setEdit({ ...edit, insurance_no: e.target.value })} data-testid={`edit-insurance-${person.id}`} /></label>
          </div>
          <Btn size="sm" variant="primary" className="mt-3" onClick={saveFields} data-testid={`save-fields-${person.id}`}>Save profile</Btn>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-2.5">
            <select className="pq-input py-1.5 flex-1" value={docType} onChange={(e) => setDocType(e.target.value)} data-testid={`upload-type-${person.id}`}>
              {DOC_TYPES.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={upload} data-testid={`upload-input-${person.id}`} />
            <Btn size="sm" variant="primary" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`upload-btn-${person.id}`}>{uploading ? <Spinner className="w-3.5 h-3.5" /> : <><Upload size={13} /> Upload</>}</Btn>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {(person.documents || []).length === 0
              ? <div className="text-[12.5px] text-slate-400 py-4 text-center col-span-2">No documents uploaded yet.</div>
              : person.documents.map((d) => <DocRow key={d.id} doc={d} onChange={onChange} />)}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Directory({ role }) {
  const isSuper = useAuth().user?.role === "superadmin";
  const [people, setPeople] = useState(null);
  const [open, setOpen] = useState(null);
  const endpoint = role === "physiotherapist" ? "/admin/physios" : "/admin/doctors";
  const load = useCallback(() => api.get(endpoint).then((r) => setPeople(r.data)), [endpoint]);
  useEffect(() => { setPeople(null); load(); }, [load]);

  const setStatus = async (p, s, override_reason) => {
    try { await api.put(`/admin/users/${p.id}/verify`, { credential_status: s, override_reason }); toast.success(`${p.name} → ${s}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update status"); }
  };
  const verify = (p) => {
    const missing = role === "physiotherapist" && p.missing_docs?.length > 0;
    if (!missing) return setStatus(p, "Verified");
    const reason = window.prompt(`${p.name} is missing ${p.missing_docs.length} required document(s). Superadmin override — enter the reason for verifying now (saved with your name):`);
    if (reason === null) return;
    if (!reason.trim()) { toast.error("A reason is required to override"); return; }
    setStatus(p, "Verified", reason.trim());
  };
  const remove = async (p) => {
    if (!window.confirm(`Permanently delete ${p.name}? This removes their account and all documents. This cannot be undone.`)) return;
    try { await api.delete(`/admin/users/${p.id}`); toast.success(`${p.name} deleted`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  if (!people) return <div className="flex justify-center py-16"><Spinner className="w-6 h-6 text-teal-600" /></div>;
  const title = role === "physiotherapist" ? "Clinician Directory (Physiotherapists)" : "Doctor Directory";

  return (
    <Card>
      <CardHead title={title} action={<Badge tone="slate">{people.length} {role === "physiotherapist" ? "clinicians" : "doctors"}</Badge>} />
      {people.length === 0 ? (
        <div className="px-5 py-10 text-center text-sm text-slate-500">No {role === "physiotherapist" ? "clinicians" : "doctors"} registered yet.</div>
      ) : people.map((p) => (
        <div key={p.id} className="border-b border-slate-100 last:border-0" data-testid={`person-${p.id}`}>
          <div className="flex items-center gap-3 px-5 py-3">
            <button onClick={() => setOpen(open === p.id ? null : p.id)} className="text-slate-400 hover:text-slate-700" data-testid={`expand-${p.id}`}>
              {open === p.id ? <ChevronDown size={17} /> : <ChevronRight size={17} />}
            </button>
            <PhotoThumb docId={p.photo_doc_id} name={p.name} />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[13.5px] text-slate-800">{p.name} <CertifiedBadge comp={p.compliance} testid={`admin-certified-${p.id}`} /> {p.tier && <span className="text-slate-400 font-normal">· {p.tier}</span>}</div>
              <div className="text-[12px] text-slate-500">{p.specialty || p.email}{p.city ? ` · ${p.city}` : ""}{p.prq_id ? ` · ${p.prq_id}` : ""}</div>
              {p.verification_override && <div className="text-[11px] text-amber-700" data-testid={`override-note-${p.id}`}>Verified by override ({p.verification_override.by}): {p.verification_override.reason}</div>}
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[12px] text-slate-500">{p.verified_count}/{p.doc_count || 0} docs</span>
              {p.missing_docs?.length > 0 && <span className="flex items-center gap-1 text-[11.5px] text-amber-600" data-testid={`missing-${p.id}`}><AlertCircle size={13} /> {p.missing_docs.length} missing</span>}
            </div>
            <Badge tone={statusTone(p.credential_status)}>{p.credential_status || "Pending"}</Badge>
            {p.credential_status !== "Verified"
              ? <Btn size="sm" className="border-emerald-300 text-emerald-700" onClick={() => verify(p)} disabled={!isSuper && role === "physiotherapist" && p.missing_docs?.length > 0} title={role === "physiotherapist" && p.missing_docs?.length > 0 ? (isSuper ? "Missing documents — superadmin override needs a reason" : "Upload all required documents first") : ""} data-testid={`verify-person-${p.id}`}>{isSuper && role === "physiotherapist" && p.missing_docs?.length > 0 ? "Verify (override)" : "Verify"}</Btn>
              : <Btn size="sm" variant="danger" onClick={() => setStatus(p, "Suspended")} data-testid={`suspend-person-${p.id}`}>Suspend</Btn>}
            <Btn size="sm" variant="danger" onClick={() => remove(p)} data-testid={`delete-person-${p.id}`}><Trash2 size={13} /></Btn>
          </div>
          {open === p.id && <PersonDetail person={p} role={role} onChange={load} />}
        </div>
      ))}
    </Card>
  );
}
